import { Button } from '../common/Button';
import type {
  TimeOffAllowanceKind,
  TimeOffCategory,
  TimeOffType,
} from '../../services/api/timeOff';

/**
 * MKS-327 — the behaviour editor for one time-off type.
 *
 * Every field here answers a question some other part of the app used to
 * answer by inspecting the type's name or code. Shared by the create form and
 * the inline edit row on `/admin/time-off-types` so the two cannot drift.
 */
export interface TimeOffTypeFormValues {
  name: string;
  code: string;
  category: TimeOffCategory;
  isPaid: boolean;
  countsTowardHoursTarget: boolean;
  allowanceKind: TimeOffAllowanceKind;
  allowanceDays: number | null;
  needsApproval: boolean;
  partialDayAllowed: boolean;
  colour: string;
  setsSlackStatus: boolean;
  slackStatusEmoji: string;
}

/** A new type behaves like today's types until the admin says otherwise. */
export const DEFAULT_TIME_OFF_TYPE_FORM: TimeOffTypeFormValues = {
  name: '',
  code: '',
  category: 'absence',
  isPaid: true,
  countsTowardHoursTarget: true,
  allowanceKind: 'none',
  allowanceDays: null,
  needsApproval: true,
  partialDayAllowed: true,
  colour: '#6366F1',
  setsSlackStatus: false,
  slackStatusEmoji: '',
};

/** Fill the form from an existing row, falling back to the same defaults. */
export function typeToForm(type: TimeOffType): TimeOffTypeFormValues {
  return {
    name: type.name ?? '',
    code: type.code ?? '',
    category: type.category ?? 'absence',
    isPaid: type.isPaid ?? true,
    countsTowardHoursTarget: type.countsTowardHoursTarget ?? true,
    allowanceKind: type.allowanceKind ?? 'none',
    allowanceDays: type.allowanceDays ?? null,
    needsApproval: type.needsApproval ?? true,
    partialDayAllowed: type.partialDayAllowed ?? true,
    colour: type.colour ?? '#6366F1',
    setsSlackStatus: type.setsSlackStatus ?? false,
    slackStatusEmoji: type.slackStatusEmoji ?? '',
  };
}

/**
 * Form values → the API body.
 *
 * Normalises the two "empty means absent" text fields to `null` (the backend
 * stores them nullable, and `''` would read as a configured-but-blank code /
 * emoji), and pins `countsTowardHoursTarget` to false for a presence type —
 * the form disables that checkbox, so sending its stale value would persist a
 * setting the UI is telling the admin does not apply. `allowanceDays` only
 * travels with the allowance kind that uses it.
 */
export function formToInput(values: TimeOffTypeFormValues) {
  const isPresence = values.category === 'presence';
  return {
    name: values.name.trim(),
    code: values.code.trim() || null,
    category: values.category,
    isPaid: values.isPaid,
    countsTowardHoursTarget: isPresence ? false : values.countsTowardHoursTarget,
    allowanceKind: values.allowanceKind,
    allowanceDays: values.allowanceKind === 'own_yearly_days' ? values.allowanceDays : null,
    needsApproval: values.needsApproval,
    partialDayAllowed: values.partialDayAllowed,
    colour: values.colour,
    setsSlackStatus: values.setsSlackStatus,
    slackStatusEmoji: values.setsSlackStatus ? values.slackStatusEmoji.trim() || null : null,
  };
}

/**
 * The one-line summary shown in the list, so an admin can see what a type does
 * without opening the editor. Only the non-default facts are listed: a row
 * reading "Absence · Paid" behaves exactly like every type did before MKS-327.
 */
export function describeBehaviour(type: TimeOffType): string {
  const parts: string[] = [
    type.category === 'presence' ? 'Presence (working)' : 'Absence',
    type.isPaid === false ? 'Unpaid' : 'Paid',
  ];

  if (type.category !== 'presence' && type.countsTowardHoursTarget === false) {
    parts.push('does not fill the hours target');
  }
  if (type.allowanceKind === 'vacation_entitlement') {
    parts.push('draws vacation entitlement');
  }
  if (type.allowanceKind === 'own_yearly_days') {
    parts.push(`${type.allowanceDays ?? 0} days a year`);
  }
  if (type.needsApproval === false) {
    parts.push('no approval');
  }
  if (type.partialDayAllowed === false) {
    parts.push('whole days only');
  }
  if (type.setsSlackStatus) {
    parts.push(`Slack status${type.slackStatusEmoji ? ` ${type.slackStatusEmoji}` : ''}`);
  }

  return parts.join(' · ');
}

interface TimeOffTypeFormProps {
  values: TimeOffTypeFormValues;
  onChange: (values: TimeOffTypeFormValues) => void;
  onSubmit: (values: TimeOffTypeFormValues) => void | Promise<void>;
  onCancel: () => void;
  submitLabel: string;
  isLoading?: boolean;
  error?: string | null;
  /** False while the MKS-327 flag is off: the form stays the name-only one. */
  showBehaviour: boolean;
}

function Checkbox({
  id,
  label,
  hint,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label htmlFor={id} className="flex items-start gap-2 text-sm text-gray-700">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600"
      />
      <span>
        <span className="font-medium">{label}</span>
        <span className="block text-xs text-gray-500">{hint}</span>
      </span>
    </label>
  );
}

export default function TimeOffTypeForm({
  values,
  onChange,
  onSubmit,
  onCancel,
  submitLabel,
  isLoading,
  error,
  showBehaviour,
}: TimeOffTypeFormProps) {
  const set = <K extends keyof TimeOffTypeFormValues>(key: K, value: TimeOffTypeFormValues[K]) => {
    onChange({ ...values, [key]: value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(values);
  };

  // A presence type's hours never fill the target — the person logs their real
  // entries for the day. The checkbox is disabled rather than hidden so the
  // rule is visible instead of looking like a bug.
  const isPresence = values.category === 'presence';

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-lg border border-gray-200 p-4 space-y-4"
    >
      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="tot-name" className="block text-sm font-medium text-gray-700 mb-1">
            Name *
          </label>
          <input
            id="tot-name"
            type="text"
            required
            value={values.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="e.g. Home office"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        {showBehaviour && (
          <div>
            <label htmlFor="tot-code" className="block text-sm font-medium text-gray-700 mb-1">
              Code
            </label>
            <input
              id="tot-code"
              type="text"
              value={values.code}
              onChange={(e) => set('code', e.target.value)}
              placeholder="home_office"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Stable identifier for imports. Behaviour comes from the settings below, not from
              this.
            </p>
          </div>
        )}
      </div>

      {showBehaviour && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label htmlFor="tot-category" className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                id="tot-category"
                value={values.category}
                onChange={(e) => set('category', e.target.value as TimeOffCategory)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="absence">Absence — not working</option>
                <option value="presence">Presence — working elsewhere</option>
              </select>
            </div>

            <div>
              <label htmlFor="tot-allowance" className="block text-sm font-medium text-gray-700 mb-1">
                Draws an allowance
              </label>
              <select
                id="tot-allowance"
                value={values.allowanceKind}
                onChange={(e) => set('allowanceKind', e.target.value as TimeOffAllowanceKind)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="none">None</option>
                <option value="vacation_entitlement">Vacation entitlement</option>
                <option value="own_yearly_days">Own yearly allowance</option>
              </select>
            </div>

            <div>
              <label htmlFor="tot-allowance-days" className="block text-sm font-medium text-gray-700 mb-1">
                Yearly allowance (days)
              </label>
              <input
                id="tot-allowance-days"
                type="number"
                min={1}
                max={365}
                disabled={values.allowanceKind !== 'own_yearly_days'}
                value={values.allowanceDays ?? ''}
                onChange={(e) =>
                  set('allowanceDays', e.target.value === '' ? null : Number(e.target.value))
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400"
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Checkbox
              id="tot-paid"
              label="Paid"
              hint="Payroll reports these hours as paid leave."
              checked={values.isPaid}
              onChange={(checked) => set('isPaid', checked)}
            />
            <Checkbox
              id="tot-counts"
              label="Counts toward the monthly hours target"
              hint={
                isPresence
                  ? 'Never for a presence type — the person logs their real hours that day.'
                  : 'Reduces the hours the person still has to log, like vacation or sick leave.'
              }
              checked={isPresence ? false : values.countsTowardHoursTarget}
              onChange={(checked) => set('countsTowardHoursTarget', checked)}
            />
            <Checkbox
              id="tot-approval"
              label="Needs approval"
              hint="Off: the request is approved on submit and the manager is notified."
              checked={values.needsApproval}
              onChange={(checked) => set('needsApproval', checked)}
            />
            <Checkbox
              id="tot-partial"
              label="Partial day allowed"
              hint="Off: the request must cover whole days."
              checked={values.partialDayAllowed}
              onChange={(checked) => set('partialDayAllowed', checked)}
            />
            <Checkbox
              id="tot-slack"
              label="Set a Slack status"
              hint="Someone on this type of time-off gets the status set automatically."
              checked={values.setsSlackStatus}
              onChange={(checked) => set('setsSlackStatus', checked)}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="tot-colour" className="block text-sm font-medium text-gray-700 mb-1">
                Colour
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="tot-colour"
                  type="color"
                  value={values.colour}
                  onChange={(e) => set('colour', e.target.value)}
                  className="h-9 w-12 rounded border border-gray-300"
                />
                <span className="text-sm text-gray-600">{values.colour}</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Used in the team calendar and the attendance report.
              </p>
            </div>
            <div>
              <label htmlFor="tot-emoji" className="block text-sm font-medium text-gray-700 mb-1">
                Slack status emoji
              </label>
              <input
                id="tot-emoji"
                type="text"
                disabled={!values.setsSlackStatus}
                value={values.slackStatusEmoji}
                onChange={(e) => set('slackStatusEmoji', e.target.value)}
                placeholder=":palm_tree:"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400"
              />
            </div>
          </div>
        </>
      )}

      <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" isLoading={isLoading}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
